import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import { withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  ILicenseTrackerAvailabilityItem,
  OrganizationLicenseTrackerLicenseStatusEnum,
  SetLicenseTrackerAvailabilityRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ensureOk from "./ensureOk";

type TrackerStatusValues = {
  status?: OrganizationLicenseTrackerLicenseStatusEnum;
};

const { Form } = withTypes<TrackerStatusValues>();

type TrackerStatusDialogProps = {
  licenseId: string;
  item: ILicenseTrackerAvailabilityItem;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

/** The three statuses the ticket offers. Planning is reserved in the lookup but not exposed. */
const STATUS_OPTIONS = [
  {
    value: OrganizationLicenseTrackerLicenseStatusEnum.Released,
    labelKey: "released",
  },
  {
    value: OrganizationLicenseTrackerLicenseStatusEnum.ComingSoon,
    labelKey: "comingSoon",
  },
  {
    value: OrganizationLicenseTrackerLicenseStatusEnum.NotPlanned,
    labelKey: "notPlanned",
  },
];

const TrackerStatusDialog = ({
  licenseId,
  item,
  open,
  onClose,
  onSaved,
}: TrackerStatusDialogProps) => {
  const { formatMessage: t } = useIntl();
  const { licenseTrackerService } = useGlobalContext();

  const handleSubmit = async (values: TrackerStatusValues) => {
    try {
      ensureOk(
        await licenseTrackerService.setLicenseTrackerAvailability(
          SetLicenseTrackerAvailabilityRequest.fromJS({
            organizationId: item.organizationId,
            licenseId,
            status: values.status,
          })
        )
      );

      onSaved();
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Form
        onSubmit={handleSubmit}
        initialValues={{ status: item.status }}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit} noValidate>
            <DialogTitle>
              {t(
                { id: "organizationLicenseTracker" },
                { organization: item.organizationName ?? item.organizationId ?? "" }
              )}
            </DialogTitle>
            <DialogContent dividers>
              <Field name="status">
                {({ input }) => (
                  <TextField
                    select
                    variant="standard"
                    fullWidth
                    label={t({ id: "roadmapStatus" })}
                    {...input}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t({ id: option.labelKey })}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Field>

              {submitError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {submitError}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button disabled={submitting} onClick={onClose}>
                {t({ id: "cancelButton" })}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={16} />}
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  );
};

export default TrackerStatusDialog;
